/**
 * The four-axis confidence model, at the surface.
 *
 * ADR-0016 keeps source reliability, information credibility, extraction confidence and
 * match probability separate in storage **and refuses to blend them**. The interface has
 * to honour that: there is no `overallConfidence()` here, and there is not meant to be
 * one. A single number would be the thing an analyst reads, and it would hide that a
 * source graded A is reporting something nothing else confirms.
 */

/** Admiralty source reliability, A (best) to F (cannot be judged). */
export type SourceReliability = 'A' | 'B' | 'C' | 'D' | 'E' | 'F';

/** Admiralty information credibility, 1 (confirmed) to 6 (cannot be judged). */
export type InformationCredibility = 1 | 2 | 3 | 4 | 5 | 6;

/** What each reliability grade means, in the words an analyst would use. */
export const RELIABILITY_MEANING: Record<SourceReliability, string> = {
  A: 'Completely reliable',
  B: 'Usually reliable',
  C: 'Fairly reliable',
  D: 'Not usually reliable',
  E: 'Unreliable',
  F: 'Reliability cannot be judged',
};

/** What each credibility grade means. */
export const CREDIBILITY_MEANING: Record<InformationCredibility, string> = {
  1: 'Confirmed by other sources',
  2: 'Probably true',
  3: 'Possibly true',
  4: 'Doubtful',
  5: 'Improbable',
  6: 'Truth cannot be judged',
};

/**
 * The token name for a reliability grade.
 *
 * Returns a role, never a colour. A component that named `green-600` would have to be
 * edited to support a second theme.
 */
export function reliabilityTone(grade: string): 'ok' | 'warn' | 'danger' | 'muted' {
  if (grade === 'A' || grade === 'B') return 'ok';
  if (grade === 'C') return 'warn';
  if (grade === 'D' || grade === 'E') return 'danger';
  return 'muted';
}

/** The token name for a credibility grade. */
export function credibilityTone(grade: number): 'ok' | 'warn' | 'danger' | 'muted' {
  if (grade <= 2) return 'ok';
  if (grade === 3) return 'warn';
  if (grade <= 5) return 'danger';
  return 'muted';
}

/**
 * Render the two Admiralty axes as the analyst-facing pair, e.g. `B2`.
 *
 * Deliberately not a score. `B2` is two independent judgements printed together, and it
 * reads that way because the alternative — averaging them — would invent a number
 * neither axis supports.
 */
export function admiraltyCode(reliability: string, credibility: number): string {
  return `${reliability}${credibility}`;
}
